import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { CanImage } from './CanImage';

type Point = { x: number; y: number };
type View = Point & { scale: number };
const INITIAL: View = { x: 0, y: 0, scale: 1 };
const MAX_SCALE = 5;
const clamp = (value: number, limit: number) => Math.max(-limit, Math.min(limit, value));

/** Pinch around the fingers' midpoint; drag the enlarged image to inspect it. */
export function ZoomableCanImage({ imageId, name }: { imageId: string | null; name: string }) {
  const viewport = useRef<HTMLDivElement>(null);
  const pointers = useRef(new Map<number, Point>());
  const viewRef = useRef<View>(INITIAL);
  const [view, setView] = useState<View>(INITIAL);

  const update = (next: View) => {
    const element = viewport.current;
    if (!element) return;
    const scale = Math.max(1, Math.min(MAX_SCALE, next.scale));
    const bounded = {
      scale,
      x: clamp(next.x, element.clientWidth * (scale - 1) / 2),
      y: clamp(next.y, element.clientHeight * (scale - 1) / 2),
    };
    viewRef.current = bounded;
    setView(bounded);
  };

  const zoom = (scale: number, anchor: Point = { x: 0, y: 0 }) => {
    const current = viewRef.current;
    const ratio = Math.max(1, Math.min(MAX_SCALE, scale)) / current.scale;
    update({ scale, x: anchor.x - (anchor.x - current.x) * ratio, y: anchor.y - (anchor.y - current.y) * ratio });
  };

  useEffect(() => {
    const element = viewport.current;
    if (!element) return;
    const observer = new ResizeObserver(() => update(viewRef.current));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const move = (event: PointerEvent<HTMLDivElement>) => {
    const previous = pointers.current.get(event.pointerId);
    if (!previous) return;
    const next = { x: event.clientX, y: event.clientY };
    const other = [...pointers.current.entries()].find(([id]) => id !== event.pointerId)?.[1];
    const current = viewRef.current;
    pointers.current.set(event.pointerId, next);
    if (other) {
      const before = Math.hypot(previous.x - other.x, previous.y - other.y);
      if (before < 1) return;
      const after = Math.hypot(next.x - other.x, next.y - other.y);
      const rect = event.currentTarget.getBoundingClientRect();
      const midpoint = {
        x: (previous.x + other.x) / 2 - rect.left - rect.width / 2,
        y: (previous.y + other.y) / 2 - rect.top - rect.height / 2,
      };
      const scale = Math.max(1, Math.min(MAX_SCALE, current.scale * after / before));
      const ratio = scale / current.scale;
      update({
        scale,
        x: midpoint.x + (next.x - previous.x) / 2 - (midpoint.x - current.x) * ratio,
        y: midpoint.y + (next.y - previous.y) / 2 - (midpoint.y - current.y) * ratio,
      });
    } else {
      update({ ...current, x: current.x + next.x - previous.x, y: current.y + next.y - previous.y });
    }
  };

  const end = (event: PointerEvent<HTMLDivElement>) => pointers.current.delete(event.pointerId);

  if (!imageId) return <div className="can-zoom__missing">Nog geen afbeelding van dit blik.</div>;

  return (
    <div className="can-zoom">
      <div
        ref={viewport}
        className="can-zoom__viewport"
        tabIndex={0}
        role="group"
        aria-label={`Afbeelding van ${name}. Knijp met twee vingers of gebruik plus en min om te zoomen. Druk op 0 om de zoom te herstellen. Versleep na inzoomen of gebruik de pijltjestoetsen.`}
        onPointerDown={(event) => {
          if (event.button !== 0 || pointers.current.size >= 2) return;
          pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        onLostPointerCapture={end}
        onDoubleClick={() => zoom(viewRef.current.scale > 1 ? 1 : 2)}
        onKeyDown={(event) => {
          const current = viewRef.current;
          if (event.key === '+' || event.key === '=') zoom(current.scale + 0.5);
          else if (event.key === '-') zoom(current.scale - 0.5);
          else if (event.key === '0') update(INITIAL);
          else if (event.key === 'ArrowLeft') update({ ...current, x: current.x + 40 });
          else if (event.key === 'ArrowRight') update({ ...current, x: current.x - 40 });
          else if (event.key === 'ArrowUp') update({ ...current, y: current.y + 40 });
          else if (event.key === 'ArrowDown') update({ ...current, y: current.y - 40 });
          else return;
          event.preventDefault();
        }}
      >
        <div className="can-zoom__image" style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}>
          <CanImage imageId={imageId} alt={name} />
        </div>
      </div>
      <p className="can-zoom__hint">Knijp om te zoomen · versleep om te bekijken</p>
    </div>
  );
}
